/**
 * "Machines in CRM": the customer's machines from the CRM installed base, each with the
 * F&O BOM link I2Q built (docs/crm-installed-base.md), read only.
 *
 * - `CrmMachinesCard`: one row per machine (product and model, machine number, site, legal
 *   entity, project) with a status chip, and on demand how the link was made. A machine
 *   the mail names comes first.
 * - `CrmLineMarker`: under a line's item, "In the BOM of machine 154321" when the item is a
 *   part of one of the customer's linked machines, or "In the BOM of machine 154321, 3 levels
 *   below 10076109110232" when it sits below one of its as-built parts (spare map).
 *
 * Display only: nothing here accepts a line, picks a customer or changes a price. The card
 * stays hidden until the CRM sync has built links and the case has a confirmed customer.
 */

import { Badge, Card, Collapsible, Typography } from "@norican/react";
import { useState } from "react";

import {
  type CrmLineLink,
  type CrmMachine,
  type CrmMachineLink,
  type CrmMachinesView,
  useCaseCrmMachines,
} from "@/lib/api";

type Label = CrmMachine["link_label"];

const CHIP: Record<
  NonNullable<Label>,
  { text: string; tone: "success" | "info" | "warning" | "neutral" }
> = {
  verified_bom: { text: "Verified BOM", tone: "success" },
  verified_context: { text: "Verified, no items", tone: "info" },
  unverified_bom: { text: "BOM not verified", tone: "warning" },
  context: { text: "BlueStar reference", tone: "neutral" },
  none: { text: "No BOM link", tone: "neutral" },
};

const SPARE_HINT =
  "Items found in the F&O and BlueStar BOMs under the as-built lines, up to 4 levels. " +
  "Built by I2Q, not a CRM or F&O record.";

const FOOTNOTE =
  "From CRM installed base; BOM link built by I2Q from F&O projects and machine items. " +
  "Not a CRM or F&O record.";

/** The chip text and tone of a machine's best link. */
export function chipOf(label: Label | undefined) {
  return CHIP[label ?? "none"] ?? CHIP.none;
}

/** "migrated project 600123 in DK", "machine items 154321", "BlueStar project 154321". */
export function linkSentence(link: CrmMachineLink): string {
  const where = link.entity ? ` in ${link.entity.toUpperCase()}` : "";
  switch (link.method) {
    case "mig_project":
      return `Migrated project ${link.key}${where}`;
    case "serial_project":
      return `Project ${link.key}${where} (from the serial number)`;
    case "name_project":
    case "bracket_project":
      return `Project ${link.key}${where} (from the machine name)`;
    case "suffix6":
    case "suffix4":
      return `Machine items ${link.ref_id ?? ""}-${link.key}${where}`.replace(" -", " ");
    case "bluestar":
      return `BlueStar project ${link.key}`;
    default:
      return `${link.method} ${link.key}${where}`;
  }
}

/** "4 production orders, 120 BOM lines" or "2 sales to this customer, 1 via group companies". */
export function evidenceSentence(link: CrmMachineLink): string {
  const e = link.evidence ?? {};
  const parts: string[] = [];
  if (e.orders) {
    parts.push(`${e.orders} production order${e.orders === 1 ? "" : "s"}`);
  }
  if (e.lines) {
    parts.push(`${e.lines} BOM line${e.lines === 1 ? "" : "s"}`);
  }
  if (e.own_sales) {
    parts.push(`${e.own_sales} sale${e.own_sales === 1 ? "" : "s"} to this customer`);
  }
  if (e.group_sales) {
    parts.push(`${e.group_sales} via group companies`);
  }
  if (e.reseller_sales) {
    parts.push(`${e.reseller_sales} via agents`);
  }
  if (e.other_customers) {
    parts.push(`${e.other_customers} other customer${e.other_customers === 1 ? "" : "s"}`);
  }
  return parts.join(", ");
}

/** "Parts: 12 as built, 47 spare parts below (sold or quoted in DK)"; null without parts. */
export function partsSentence(machine: CrmMachine): string | null {
  const built = machine.n_parts ?? 0;
  const below = machine.n_spare_parts ?? 0;
  if (built === 0 && below === 0) {
    return null;
  }
  const where = machine.entity ? ` in ${machine.entity.toUpperCase()}` : "";
  const spare =
    below > 0
      ? `, ${below} spare part${below === 1 ? "" : "s"} below (sold or quoted${where})`
      : "";
  return `Items: ${built} as built${spare}`;
}

/** ", 3 levels below 10076109110232 (BlueStar spare line)" or ", current number of 9116X0151". */
export function sparePathSentence(path: NonNullable<CrmLineLink["spare_path"]>): string {
  if (path.depth <= 0) {
    return `, current number of ${path.root_item}`;
  }
  const levels = path.depth === 1 ? "1 level" : `${path.depth} levels`;
  const extra = [
    path.bs_spare ? "BlueStar spare line" : null,
    path.renamed ? "renumbered" : null,
  ].filter(Boolean);
  return `, ${levels} below ${path.root_item}${extra.length ? ` (${extra.join(", ")})` : ""}`;
}

/** "DISAMATIC D3", else the product or "Machine". */
export function machineTitle(machine: CrmMachine): string {
  return [machine.product, machine.model].filter(Boolean).join(" ") || "Machine";
}

function MachineRow({ machine }: { machine: CrmMachine }) {
  const [open, setOpen] = useState(false);
  const links = machine.links ?? [];
  const nParts = machine.n_parts ?? 0;
  const parts = partsSentence(machine);
  const chip = chipOf(machine.link_label);
  const facts = [
    machine.serial || machine.machine_no ? `No. ${machine.serial ?? machine.machine_no}` : null,
    machine.site,
    machine.entity?.toUpperCase(),
    machine.project ? `project ${machine.project}` : null,
  ].filter(Boolean);
  return (
    <li
      className="border-border-subtle flex flex-col gap-1 border-b pb-2 last:border-b-0"
      data-crm-asset={machine.asset_id}
    >
      <span className="flex flex-wrap items-center gap-2">
        <Typography as="span" variant="label" className="text-fg">
          {machineTitle(machine)}
        </Typography>
        <Badge tone={chip.tone} variant="subtle" size="xs" data-crm-label={machine.link_label}>
          {chip.text}
        </Badge>
        {machine.named_in_mail && (
          <Badge tone="info" variant="outline" size="xs">
            Named in the mail
          </Badge>
        )}
      </span>
      {facts.length > 0 && (
        <span className="text-fg-muted text-xs break-words">{facts.join(" · ")}</span>
      )}
      {parts && (
        <span
          className="text-fg-muted text-xs"
          title={(machine.n_spare_parts ?? 0) > 0 ? SPARE_HINT : undefined}
          data-crm-spare-parts={machine.n_spare_parts ?? 0}
        >
          {parts}
        </span>
      )}
      {links.length > 0 && (
        <Collapsible.Root open={open} onOpenChange={setOpen}>
          <Collapsible.Trigger className="text-accent-fg-subtle w-fit text-xs hover:underline">
            {open ? "Hide the link" : "Show the link"}
          </Collapsible.Trigger>
          <Collapsible.Panel>
            <ul className="mt-1 flex flex-col gap-0.5 text-xs">
              {links.map((link) => (
                <li key={`${link.tier}-${link.method}-${link.key}`} className="text-fg-muted">
                  {linkSentence(link)}
                  {link.verified ? ", customer checked" : ", not verified"}
                  {evidenceSentence(link) ? ` (${evidenceSentence(link)})` : ""}
                </li>
              ))}
              {nParts > 0 && <li className="text-fg-muted">{nParts} parts in the linked BOM</li>}
            </ul>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
    </li>
  );
}

/** True when the panel has something to show. */
export function hasMachines(view: CrmMachinesView | undefined): view is CrmMachinesView {
  return Boolean(view?.enabled && view.available && (view.machines ?? []).length > 0);
}

/** The "Machines in CRM" card on the case page (hidden while there is nothing to show). */
export function CrmMachinesCard({ caseId }: { caseId: string }) {
  const { data } = useCaseCrmMachines(caseId);
  if (!hasMachines(data)) {
    return null;
  }
  const machines = data.machines ?? [];
  const linked = machines.filter((m) => m.best_tier).length;
  return (
    <Card variant="outline" padding="none" data-crm-machines={machines.length}>
      <Card.Header padding="sm" className="border-border-subtle flex items-center border-b">
        <Typography variant="label">Machines in CRM</Typography>
        <span className="text-fg-subtle ml-auto text-xs">
          {machines.length} machines, {linked} linked to F&amp;O
        </span>
      </Card.Header>
      <Card.Body className="flex flex-col gap-2">
        <ul className="flex flex-col gap-2">
          {machines.map((m) => (
            <MachineRow key={m.asset_id} machine={m} />
          ))}
        </ul>
        <Typography variant="caption" tone="subtle">
          {FOOTNOTE}
        </Typography>
      </Card.Body>
    </Card>
  );
}

/** "In the BOM of machine 154321" under a line's item; nothing when the item is in none. */
export function CrmLineMarker({ caseId, lineIndex }: { caseId: string; lineIndex: number }) {
  const { data } = useCaseCrmMachines(caseId);
  if (!hasMachines(data) || lineIndex < 0) {
    return null;
  }
  const hit = (data.line_links ?? []).find((l) => l.line_index === lineIndex);
  const machines = hit?.machines ?? [];
  if (machines.length === 0) {
    return null;
  }
  const names = machines.slice(0, 2).join(", ");
  const more = machines.length > 2 ? ` and ${machines.length - 2} more` : "";
  const path = hit?.spare_path;
  return (
    <span
      className="text-fg-muted mt-1 block text-xs"
      data-crm-line={lineIndex}
      title={path ? SPARE_HINT : undefined}
    >
      In the BOM of machine {names}
      {more}
      {path ? sparePathSentence(path) : ""}
    </span>
  );
}
